import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { getStructuredData, serializeSchema, SITE_URL } from './schema'

export interface RouteSeo {
  title: string
  description: string
  robots: 'index, follow' | 'noindex, nofollow'
  ogType: 'website'
}

export const routeSeo: Record<string, RouteSeo> = {
  '/': {
    title: 'Andrea Villalobos | GLP-1 Accountability & Longevity Support',
    description: 'Meet Andrea Villalobos, a GLP-1 Accountability & Longevity Guide offering non-medical, remote accountability and lifestyle support alongside licensed clinical care.',
    robots: 'index, follow',
    ogType: 'website',
  },
  '/main': {
    title: 'Remote GLP-1 Accountability Support | Andrea Villalobos',
    description: 'Explore Andrea Villalobos\' remote, non-medical GLP-1 accountability and lifestyle support for routines, consistency, and longevity goals alongside your licensed care team.',
    robots: 'index, follow',
    ogType: 'website',
  },
  '/terms-of-service': {
    title: 'Terms of Service | Andrea Villalobos',
    description: 'Read the terms governing use of the Andrea Villalobos website and its informational, non-medical GLP-1 accountability resources.',
    robots: 'index, follow',
    ogType: 'website',
  },
  '/disclaimer': {
    title: 'Health Disclaimer | Andrea Villalobos',
    description: 'Review Andrea Villalobos\' health disclaimer explaining that her remote GLP-1 accountability support is non-medical and not a substitute for licensed clinical care.',
    robots: 'index, follow',
    ogType: 'website',
  },
  '/admin': {
    title: 'Admin | Andrea Villalobos',
    description: 'Private content administration for Andrea Villalobos.',
    robots: 'noindex, nofollow',
    ogType: 'website',
  },
  '/admin-login': {
    title: 'Admin Login | Andrea Villalobos',
    description: 'Private administration login for Andrea Villalobos.',
    robots: 'noindex, nofollow',
    ogType: 'website',
  },
  '/thank-you': {
    title: 'Thank You | Andrea Villalobos',
    description: 'Confirmation page for Andrea Villalobos website inquiries.',
    robots: 'noindex, nofollow',
    ogType: 'website',
  },
  '/form': {
    title: 'Contact Form | Andrea Villalobos',
    description: 'Private contact form for Andrea Villalobos inquiries.',
    robots: 'noindex, nofollow',
    ogType: 'website',
  },
}

export const notFoundSeo: RouteSeo = {
  title: 'Page Not Found | Andrea Villalobos',
  description: 'This page does not exist on the Andrea Villalobos website.',
  robots: 'noindex, nofollow',
  ogType: 'website',
}

/** Virtual path used when prerendering dist/404.html for the static host. */
export const NOT_FOUND_PATH = '/404'

export function normalizePathname(pathname: string): string {
  const path = pathname.replace(/\/$/, '')
  return path || '/'
}

export function getRouteSeo(pathname: string): RouteSeo {
  const normalizedPath = normalizePathname(pathname)
  if (normalizedPath === NOT_FOUND_PATH) {
    return notFoundSeo
  }
  return routeSeo[normalizedPath] || notFoundSeo
}

export function getCanonicalUrl(pathname: string): string {
  const normalizedPath = normalizePathname(pathname)
  if (!(normalizedPath in routeSeo) || normalizedPath === NOT_FOUND_PATH) {
    // Avoid soft-canonicalizing unknown or 404 documents to the homepage.
    return `${SITE_URL}${NOT_FOUND_PATH}`
  }
  return normalizedPath === '/' ? SITE_URL : `${SITE_URL}${normalizedPath}`
}

const socialImageUrl = `${SITE_URL}/images/og-andrea-villalobos.png`

export function RouteSeo() {
  const { pathname } = useLocation()

  useEffect(() => {
    document.head.querySelectorAll('title, [data-route-seo]').forEach((element) => element.remove())
    document.head.insertAdjacentHTML('beforeend', renderSeoHead(pathname))
  }, [pathname])

  return null
}

export function renderSeoHead(pathname: string): string {
  const seo = getRouteSeo(pathname)
  const canonicalUrl = getCanonicalUrl(pathname)
  const structuredData = getStructuredData(normalizePathname(pathname))
  const meta = [
    `<meta data-route-seo="true" name="description" content="${escapeAttribute(seo.description)}">`,
    `<meta data-route-seo="true" name="robots" content="${seo.robots}">`,
    `<link data-route-seo="true" rel="canonical" href="${canonicalUrl}">`,
    `<meta data-route-seo="true" property="og:type" content="${seo.ogType}">`,
    '<meta data-route-seo="true" property="og:site_name" content="Andrea Villalobos">',
    `<meta data-route-seo="true" property="og:title" content="${escapeAttribute(seo.title)}">`,
    `<meta data-route-seo="true" property="og:description" content="${escapeAttribute(seo.description)}">`,
    `<meta data-route-seo="true" property="og:url" content="${canonicalUrl}">`,
    `<meta data-route-seo="true" property="og:image" content="${socialImageUrl}">`,
    '<meta data-route-seo="true" property="og:image:width" content="1200">',
    '<meta data-route-seo="true" property="og:image:height" content="630">',
    '<meta data-route-seo="true" property="og:image:alt" content="Andrea Villalobos, GLP-1 Accountability &amp; Longevity Guide">',
    '<meta data-route-seo="true" name="twitter:card" content="summary_large_image">',
    `<meta data-route-seo="true" name="twitter:title" content="${escapeAttribute(seo.title)}">`,
    `<meta data-route-seo="true" name="twitter:description" content="${escapeAttribute(seo.description)}">`,
    `<meta data-route-seo="true" name="twitter:image" content="${socialImageUrl}">`,
    ...structuredData.map((schema) => `<script data-route-seo="true" type="application/ld+json">${serializeSchema(schema)}</script>`),
  ]

  return `<title data-route-seo="true">${escapeAttribute(seo.title)}</title>${meta.join('')}`
}

function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}
